import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

// Keyframe Animations
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
  50% { box-shadow: 0 0 30px rgba(255, 215, 0, 0.5); }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 120px 20px 80px;
  position: relative;
  overflow: hidden;
  color: white;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.05) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 200%;
    height: 100%;
    top: 0;
    left: -50%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 215, 0, 0.03) 45%, 
      rgba(255, 215, 0, 0.06) 50%, 
      rgba(255, 215, 0, 0.03) 55%, 
      transparent 100%
    );
    animation: ${shimmer} 15s infinite linear;
    pointer-events: none;
    z-index: 1;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 100px 15px 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const PageTitle = styled.h1`
  font-size: clamp(42px, 6vw, 72px);
  font-weight: 900;
  margin-bottom: 30px;
  letter-spacing: -2px;
  text-align: center;
  line-height: 1.1;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
    animation: ${pulse} 3s ease-in-out infinite;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(32px, 8vw, 48px);
    letter-spacing: -1px;
  }
`;

const PageSubtitle = styled.p`
  font-size: clamp(20px, 3vw, 28px);
  color: rgba(255, 255, 255, 0.95);
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.5;
  text-align: center;
  font-weight: 400;
  opacity: 0.95;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 4vw, 22px);
    margin-bottom: 40px;
  }
`;

const StorySection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  margin-bottom: 80px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StoryContent = styled.div``;

const SectionLabel = styled.div`
  color: #ffd700;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 20px;
`;

const StoryTitle = styled.h2`
  font-size: clamp(36px, 5vw, 52px);
  font-weight: 900;
  color: #1a2332;
  margin-bottom: 30px;
  line-height: 1.2;
  letter-spacing: -1.5px;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
`;

const StoryText = styled.p`
  font-size: 18px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 25px;
`;

const StoryHighlight = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  padding: 30px;
  border-radius: 20px;
  margin: 30px 0;
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.3);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(255, 215, 0, 0.4);
  }
`;

const HighlightText = styled.p`
  color: #1a2332;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.6;
  margin: 0;
  font-style: italic;
`;

const StoryImage = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  padding: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  position: relative;
  
  img {
    max-width: 100%;
    border-radius: 12px;
  }
`;

const Timeline = styled.div`
  margin-top: 80px;
`;

const TimelineTitle = styled.h3`
  font-size: 36px;
  font-weight: 800;
  color: #333;
  text-align: center;
  margin-bottom: 60px;
`;

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const TimelineCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 30px;
  border-radius: 20px;
  text-align: center;
  position: relative;
  border: 3px solid transparent;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, #ffd700, #ffa500);
    border-radius: 50%;
    box-shadow: 0 4px 10px rgba(255, 215, 0, 0.3);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    border-color: rgba(255, 215, 0, 0.3);
  }
`;

const TimelineYear = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #232B3B;
  margin-bottom: 10px;
`;

const TimelineEvent = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const StatsSection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 100px 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(16, 163, 127, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 20px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ delay?: string }>`
  text-align: center;
  color: white;
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  position: relative;
  z-index: 1;
`;

const StatNumber = styled.div`
  font-size: clamp(36px, 5vw, 48px);
  font-weight: 900;
  color: #ffd700;
  text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  margin-bottom: 5px;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: clamp(12px, 1.5vw, 14px);
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 1px;
  font-weight: 600;
`;

const StatDescription = styled.p`
  font-size: 14px;
  opacity: 0.7;
  margin-top: 10px;
`;

const ValuesSection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 50%, #fff9e6 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

const ValuesTitle = styled.h2`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 900;
  color: #1a2332;
  text-align: center;
  margin-bottom: 60px;
  letter-spacing: -1.5px;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
`;

const ValueCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 40px;
  border-radius: 25px;
  border: 3px solid transparent;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 25px;
    padding: 3px;
    background: linear-gradient(135deg, #ffd700, #ffa500);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
    
    &::before {
      opacity: 0.5;
    }
  }
`;

const ValueIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  border-radius: 50%;
  margin: 0 auto 25px;
  position: relative;
  box-shadow: 0 8px 20px rgba(255, 215, 0, 0.3);
  animation: ${pulse} 3s ease-in-out infinite;
  
  &::after {
    content: '';
    position: absolute;
    width: 35px;
    height: 35px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const ValueTitle = styled.h3`
  font-size: 26px;
  font-weight: 800;
  color: #1a2332;
  margin-bottom: 15px;
  letter-spacing: -0.5px;
`;

const ValueText = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
`;

const TeamSection = styled.section`
  padding: 100px 20px;
  background: white;
`;

const TeamHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const TeamTitle = styled.h2`
  font-size: clamp(36px, 5vw, 52px);
  font-weight: 900;
  color: #1a2332;
  margin-bottom: 20px;
  letter-spacing: -1.5px;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
`;

const TeamSubtitle = styled.p`
  font-size: 20px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
`;

const LeadershipCard = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 50px;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const LeaderPhoto = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 25px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  border: 3px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
    border-color: rgba(255, 215, 0, 0.4);
  }
`;

const LeaderInfo = styled.div``;

const LeaderName = styled.h3`
  font-size: 36px;
  font-weight: 900;
  color: #1a2332;
  margin-bottom: 10px;
  letter-spacing: -1px;
`;

const LeaderTitle = styled.div`
  font-size: 20px;
  color: #ffd700;
  font-weight: 600;
  margin-bottom: 25px;
`;

const LeaderBio = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.7;
  margin-bottom: 20px;
`;

const TransparencySection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

const TransparencyBox = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  padding: 50px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 30px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  border: 3px solid rgba(255, 215, 0, 0.2);
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 35px 80px rgba(0, 0, 0, 0.2);
  }
`;

const TransparencyTitle = styled.h3`
  font-size: clamp(32px, 4vw, 42px);
  font-weight: 900;
  color: #1a2332;
  margin-bottom: 20px;
  letter-spacing: -1px;
`;

const TransparencyText = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 30px;
`;

const TransparencyLink = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 16px;
  transition: all 0.3s;
  
  &:hover {
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(35, 43, 59, 0.3);
    background: linear-gradient(135deg, #1a1f2e 0%, #232B3B 100%);
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 120px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(16, 163, 127, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 80px 20px;
  }
`;

const CTATitle = styled.h2`
  font-size: clamp(42px, 5vw, 64px);
  font-weight: 900;
  color: white;
  margin-bottom: 24px;
  letter-spacing: -1px;
  line-height: 1.1;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(32px, 6vw, 42px);
  }
`;

const CTASubtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 28px);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 60px;
  line-height: 1.5;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 3vw, 22px);
    margin-bottom: 40px;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #1a1a1a;
  padding: 22px 50px;
  border: none;
  border-radius: 50px;
  font-size: clamp(18px, 2.5vw, 22px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 20px 50px rgba(255, 215, 0, 0.5);
    
    &::before {
      width: 400px;
      height: 400px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 18px 35px;
    font-size: clamp(16px, 3vw, 18px);
  }
`;

export const About: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  
  return (
    <SharedLayoutUltimate>
      <HeroSection>
        <Container>
          <PageTitle>
            <span className="gold">Three Generations</span> of Trust
          </PageTitle>
          <PageSubtitle>
            The Averill family has been helping Americans secure their retirement since 1963
          </PageSubtitle>
        </Container>
      </HeroSection>

      <StorySection>
        <Container>
          <StoryGrid>
            <StoryContent>
              <SectionLabel>Our Story</SectionLabel>
              <StoryTitle>
                From Toledo to <span className="gold">Nationwide Trust</span>
              </StoryTitle>
              <StoryText>
                What started as a small insurance practice in Toledo, Ohio has grown into one of 
                America's most trusted names in retirement planning. Cap Averill II & Associates 
                has helped over 10,000 families navigate the complex world of annuities.
              </StoryText>
              <StoryText>
                We've maintained a perfect record - zero complaints with the Better Business Bureau, 
                an A+ rating, and thousands of satisfied clients who trust us with their life savings.
              </StoryText>
              <StoryHighlight>
                <HighlightText>
                  "We're not here to tell you what to do with your money. We're here to show you 
                  the truth about your options." - Cap Averill II
                </HighlightText>
              </StoryHighlight>
            </StoryContent>
            <StoryImage>
              <img 
                src="/650f4bc09c7332290b7eb7c3_AverillBuilding.png"
                alt="Averill Building"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '12px'
                }}
              />
            </StoryImage>
          </StoryGrid>

          <Timeline>
            <TimelineTitle>Our Journey</TimelineTitle>
            <TimelineGrid>
              <TimelineCard>
                <TimelineYear>1963</TimelineYear>
                <TimelineEvent>
                  Averill family enters the insurance business in Toledo, Ohio
                </TimelineEvent>
              </TimelineCard>
              <TimelineCard>
                <TimelineYear>1985</TimelineYear>
                <TimelineEvent>
                  Cap Averill II joins the family business, bringing fresh perspective
                </TimelineEvent>
              </TimelineCard>
              <TimelineCard>
                <TimelineYear>2000</TimelineYear>
                <TimelineEvent>
                  Developed the proprietary Averill Method for annuity evaluation
                </TimelineEvent>
              </TimelineCard>
              <TimelineCard>
                <TimelineYear>2020</TimelineYear>
                <TimelineEvent>
                  Launched AI-powered tools to bring transparency to the industry
                </TimelineEvent>
              </TimelineCard>
            </TimelineGrid>
          </Timeline>
        </Container>
      </StorySection>

      <StatsSection>
        <Container>
          <StatsGrid>
            <StatCard delay="0s">
              <StatNumber>10K+</StatNumber>
              <StatLabel>Happy Clients</StatLabel>
              <StatDescription>Families trust us nationwide</StatDescription>
            </StatCard>
            <StatCard delay="0.2s">
              <StatNumber>60+</StatNumber>
              <StatLabel>Years Experience</StatLabel>
              <StatDescription>Three generations strong</StatDescription>
            </StatCard>
            <StatCard delay="0.4s">
              <StatNumber>Zero</StatNumber>
              <StatLabel>Complaints</StatLabel>
              <StatDescription>BBB A+ accredited</StatDescription>
            </StatCard>
            <StatCard delay="0.6s">
              <StatNumber>100%</StatNumber>
              <StatLabel>Transparent</StatLabel>
              <StatDescription>We show our own portfolios</StatDescription>
            </StatCard>
          </StatsGrid>
        </Container>
      </StatsSection>

      <ValuesSection>
        <Container>
          <ValuesTitle>
            What Makes Us <span className="gold">Different</span>
          </ValuesTitle>
          <ValuesGrid>
            <ValueCard>
              <ValueIcon />
              <ValueTitle>Complete Transparency</ValueTitle>
              <ValueText>
                We're the only firm that shows you our own annuity portfolios. If it's good 
                enough for our money, it's good enough for yours.
              </ValueText>
            </ValueCard>
            <ValueCard>
              <ValueIcon />
              <ValueTitle>No Hidden Agenda</ValueTitle>
              <ValueText>
                We get paid in the same manner regardless of which annuity you choose - through 
                commissions from the insurance company. Our only incentive is finding what's truly 
                best for you.
              </ValueText>
            </ValueCard>
            <ValueCard>
              <ValueIcon />
              <ValueTitle>Data-Driven Decisions</ValueTitle>
              <ValueText>
                Our AI tools analyze real historical data, not hypothetical projections. See 
                actual performance, not sales pitches.
              </ValueText>
            </ValueCard>
            <ValueCard>
              <ValueIcon />
              <ValueTitle>Fiduciary Mindset</ValueTitle>
              <ValueText>
                While not legally required for annuities, we operate with a fiduciary mindset - 
                your interests always come first.
              </ValueText>
            </ValueCard>
            <ValueCard>
              <ValueIcon />
              <ValueTitle>Education First</ValueTitle>
              <ValueText>
                We believe an educated client makes the best decisions. That's why all our tools 
                and resources are completely free.
              </ValueText>
            </ValueCard>
            <ValueCard>
              <ValueIcon />
              <ValueTitle>Proven Excellence</ValueTitle>
              <ValueText>
                Our track record speaks for itself: thousands of satisfied clients and not a 
                single complaint in 40 years.
              </ValueText>
            </ValueCard>
          </ValuesGrid>
        </Container>
      </ValuesSection>

      <TransparencySection>
        <Container>
          <TransparencyBox>
            <TransparencyTitle>Our Commitment to Transparency</TransparencyTitle>
            <TransparencyText>
              We believe you deserve to know exactly how we're compensated. We don't charge you any fees - 
              we're paid directly by insurance companies when you purchase an annuity. This means our success 
              is tied to your satisfaction.
            </TransparencyText>
            {/* <TransparencyLink to="/how-we-get-paid">
              Learn More About How We Get Paid →
            </TransparencyLink> */}
          </TransparencyBox>
        </Container>
      </TransparencySection>

      <TeamSection>
        <Container>
          <TeamHeader>
            <TeamTitle><span className="gold">Leadership</span></TeamTitle>
            <TeamSubtitle>
              Meet the team dedicated to revolutionizing retirement planning
            </TeamSubtitle>
          </TeamHeader>
          
          <LeadershipCard>
            <LeaderPhoto>
              <img 
                src="/Cap/Cap.png"
                alt="Cap Averill II"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '20px'
                }}
              />
            </LeaderPhoto>
            <LeaderInfo>
              <LeaderName>Cap Averill II</LeaderName>
              <LeaderTitle>Founder & CEO</LeaderTitle>
              <LeaderBio>
                Second-generation insurance professional with over 35 years of experience. 
                Cap has personally helped thousands of families secure their retirement and 
                is the architect behind the Averill Method.
              </LeaderBio>
              <LeaderBio>
                A frequent speaker at industry conferences and a passionate advocate for 
                transparency in the annuity industry. Cap's mission is simple: help people 
                make informed decisions about their retirement.
              </LeaderBio>
            </LeaderInfo>
          </LeadershipCard>
        </Container>
      </TeamSection>

      <CTASection>
        <Container>
          <CTATitle>Ready to Experience the Difference?</CTATitle>
          <CTASubtitle>
            Join thousands of satisfied clients who've discovered a better way to plan for retirement
          </CTASubtitle>
          <CTAButton onClick={() => openCalendly()}>
            Schedule Your Free Consultation
          </CTAButton>
        </Container>
      </CTASection>
    </SharedLayoutUltimate>
  );
};